import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Blocking re-accept dialog. When mounted, it queries /eula/status; if the
 * caller hasn't accepted the CURRENT version, it renders a non-dismissable
 * dialog (no close button, no ESC/outside-click close). The user must tick
 * the checkbox and submit before they can continue using the app.
 */
export default function EulaReacceptModal() {
    const [needs, setNeeds] = useState(false);
    const [version, setVersion] = useState("");
    const [checked, setChecked] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        api.get("/eula/status")
            .then((r) => {
                if (cancelled) return;
                if (r.data.requires_reaccept) {
                    setNeeds(true);
                    setVersion(r.data.current_version);
                }
            })
            .catch(() => { /* silent — not blocking */ });
        return () => { cancelled = true; };
    }, []);

    const accept = async () => {
        if (!checked) return;
        setSaving(true);
        try {
            await api.post("/eula/accept");
            toast.success("Thanks — agreement acknowledged.");
            setNeeds(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    if (!needs) return null;

    return (
        <Dialog open={needs} onOpenChange={() => { /* non-dismissable */ }}>
            <DialogContent
                className="sm:max-w-md"
                data-testid="eula-reaccept-modal"
                onEscapeKeyDown={(e) => e.preventDefault()}
                onPointerDownOutside={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
            >
                <DialogHeader>
                    <div className="mx-auto h-11 w-11 rounded-full bg-[#0F1E3D] text-white flex items-center justify-center mb-2">
                        <ShieldCheck size={22} weight="fill" />
                    </div>
                    <DialogTitle className="font-serif text-2xl text-center">Please acknowledge our End-User Agreement</DialogTitle>
                    <DialogDescription className="text-center text-slate-600">
                        We&apos;ve updated the agreement. Before you continue, please confirm you understand that
                        <strong className="text-slate-900"> LienLift Direct is NOT a law firm</strong> and does not
                        provide legal advice.
                    </DialogDescription>
                </DialogHeader>

                <label className="flex items-start gap-2.5 text-sm text-slate-700 leading-snug cursor-pointer select-none mt-2 p-3 rounded-md border border-slate-200 bg-slate-50">
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => setChecked(e.target.checked)}
                        data-testid="eula-reaccept-checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#0F1E3D] focus:ring-[#0F1E3D] shrink-0"
                    />
                    <span>
                        I acknowledge that LienLift Direct is a document-preparation and information service —
                        NOT a law firm — and I have read and agree to the{" "}
                        <Link
                            to="/eula"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#2563EB] hover:underline font-medium"
                            data-testid="eula-reaccept-link"
                        >
                            End-User Agreement
                        </Link>.
                    </span>
                </label>

                <p className="text-[11px] text-slate-400 text-center mt-1 font-mono">Version: {version}</p>

                <DialogFooter>
                    <Button
                        onClick={accept}
                        disabled={!checked || saving}
                        className="w-full bg-[#0F1E3D] hover:bg-[#1A2C57] disabled:opacity-50"
                        data-testid="eula-reaccept-submit"
                    >
                        {saving ? "Saving…" : "I agree — continue"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
